import {
  ActionIcon,
  type ActionIconProps,
  Button,
  Checkbox,
  Flex,
  Group,
  Menu,
  Text,
  Tooltip,
  type MenuProps,
} from '@mantine/core';
import {
  ArticleNyTimesIcon,
  CheckIcon,
  CopyIcon,
  FileMdIcon,
  type IconWeight,
} from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  AnalyticsAction,
  AnalyticsCategory,
  ChatbotCopyType,
  useAnalytics,
} from 'lib/analytics';
import TRACKING_IDS from 'tracking-ids';

import { copyMessageToClipboard } from '../utils/copy-message.utils';

import type { CitationSource } from '../types';
import type { CopyFormat } from '../utils/copy-message.utils';
import type { ReactNode } from 'react';

const COPY_FEEDBACK_DURATION = 2000;

type CopyFeedbackProps = {
  successMessage?: string;
  error?: string;
};

function CopyFeedback({ successMessage, error }: CopyFeedbackProps) {
  if (error) {
    return (
      <Group gap="xs">
        <Text size="md" c="red">
          {error}
        </Text>
      </Group>
    );
  }

  if (successMessage) {
    return (
      <Group gap="xs">
        <CheckIcon size={16} />
        <Text size="md">{successMessage}</Text>
      </Group>
    );
  }

  return null;
}

export type CopyMessageActionProps = {
  messageText: string;
  sources: CitationSource[];
  sessionId: string;
  messageId: string;
  getRenderedHtml?: () => string;
  /** Optional classNames to apply to the copy menu */
  menuClassNames?: MenuProps['classNames'];
  /** Show the copy icon alone, with a tooltip, instead of a "Copy" label */
  iconOnly?: boolean;
  /** Icon-only button size. Defaults to Mantine's `md` (40px). */
  iconButtonSize?: ActionIconProps['size'];
  /** Icon size; defaults to 20px. */
  iconSize?: number;
  /** Icon weight; defaults to Phosphor's regular. */
  iconWeight?: IconWeight;
  /** Extra class for the icon button(s), e.g. to set a resting color. */
  buttonClassName?: string;
};

export default function CopyMessageAction({
  messageText,
  sources,
  sessionId,
  messageId,
  getRenderedHtml,
  menuClassNames,
  iconOnly = false,
  iconButtonSize = 'md',
  iconSize = 20,
  iconWeight,
  buttonClassName,
}: CopyMessageActionProps): ReactNode {
  const { logEvent } = useAnalytics();

  const [copySuccessMessage, setCopySuccessMessage] = useState<string | null>(
    null,
  );
  const [copyError, setCopyError] = useState<string | null>(null);
  const [includeSources, setIncludeSources] = useState(true);
  const feedbackTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (feedbackTimeout.current !== null) {
        clearTimeout(feedbackTimeout.current);
      }
    },
    [],
  );

  const resetFeedbackLater = useCallback((): void => {
    if (feedbackTimeout.current !== null) {
      clearTimeout(feedbackTimeout.current);
    }
    feedbackTimeout.current = setTimeout(() => {
      setCopySuccessMessage(null);
      setCopyError(null);
      feedbackTimeout.current = null;
    }, COPY_FEEDBACK_DURATION);
  }, []);

  const handleCopy = useCallback(
    async (format: CopyFormat): Promise<void> => {
      try {
        if (feedbackTimeout.current !== null) {
          clearTimeout(feedbackTimeout.current);
          feedbackTimeout.current = null;
        }
        setCopyError(null);
        setCopySuccessMessage(null);

        const htmlContent = getRenderedHtml?.();

        await copyMessageToClipboard(
          messageText,
          sources,
          format,
          includeSources,
          htmlContent,
        );

        setCopySuccessMessage('Message copied');
        resetFeedbackLater();

        logEvent({
          event: {
            category: AnalyticsCategory.chatbotCopyAction,
            action: AnalyticsAction.chatbotCopy,
          },
          data: {
            chat_session: { sessionId },
            chat_message: { messageId },
            copyType:
              format === 'markdown'
                ? ChatbotCopyType.markdown
                : ChatbotCopyType.richText,
            // Per the chatbot_copy_action schema, these describe the copied
            // content, not the message: an unticked "Include sources" copies
            // no citations even when the answer has them.
            hasSources: includeSources && sources.length > 0,
            sourceCount: includeSources ? sources.length : 0,
          },
        });
      } catch (error) {
        const errorMessage = 'Failed to copy to clipboard';
        setCopyError(errorMessage);
        resetFeedbackLater();

        // eslint-disable-next-line no-console
        console.error(errorMessage, error);
      }
    },
    [
      messageText,
      sources,
      sessionId,
      messageId,
      logEvent,
      getRenderedHtml,
      includeSources,
      resetFeedbackLater,
    ],
  );

  const feedbackLabel = copyError ?? copySuccessMessage;

  if (!iconOnly && feedbackLabel) {
    return (
      <CopyFeedback
        successMessage={copySuccessMessage ?? undefined}
        error={copyError ?? undefined}
      />
    );
  }

  return (
    <Menu
      width={200}
      withinPortal
      position="bottom-start"
      shadow="sm"
      classNames={menuClassNames}
    >
      {iconOnly ? (
        <Tooltip
          label={feedbackLabel ?? 'Copy'}
          position="top"
          withArrow
          openDelay={300}
          opened={feedbackLabel ? true : undefined}
        >
          <Menu.Target>
            <ActionIcon
              variant="transparent"
              size={iconButtonSize}
              className={buttonClassName}
              aria-label="Copy"
              data-tracking-id={TRACKING_IDS.hatch.chat.answer.copy}
            >
              <CopyIcon size={iconSize} weight={iconWeight} />
            </ActionIcon>
          </Menu.Target>
        </Tooltip>
      ) : (
        <Menu.Target>
          <Button
            variant="transparent"
            size="compact-sm"
            leftSection={<CopyIcon size={16} />}
            title="Copy"
            data-tracking-id={TRACKING_IDS.hatch.chat.answer.copy}
          >
            Copy
          </Button>
        </Menu.Target>
      )}
      <Menu.Dropdown>
        <Menu.Item
          leftSection={<ArticleNyTimesIcon size={16} />}
          onClick={() => void handleCopy('rich-text')}
          data-tracking-id={TRACKING_IDS.hatch.chat.answer.copyText}
        >
          Copy rich text
        </Menu.Item>
        <Menu.Item
          leftSection={<FileMdIcon size={16} />}
          onClick={() => void handleCopy('markdown')}
          data-tracking-id={TRACKING_IDS.hatch.chat.answer.copyWithSources}
        >
          Copy markdown
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item
          closeMenuOnClick={false}
          onClick={() => setIncludeSources((current) => !current)}
          data-tracking-id={TRACKING_IDS.hatch.chat.answer.copyIncludeSources}
        >
          <Flex align="center" gap="sm">
            <Checkbox
              checked={includeSources}
              readOnly
              styles={{
                root: { pointerEvents: 'none' },
                input: { cursor: 'inherit' },
              }}
            />
            <Text
              size="sm"
              c={
                includeSources
                  ? 'var(--klue-color-ink)'
                  : 'var(--klue-color-muted)'
              }
            >
              Include sources
            </Text>
          </Flex>
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
